import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from 'apps/piqet-angular/src/environments/environment';
import { ClipboardHelper } from '@piqet-angular/shared';

export interface ApiField {
  name: string;
  dataType: string;
  required: boolean;
  description: string;
  example: string;
  default?: string;
  range?: string;
  hasMoreDetails?: boolean;
  moreDetailsSection?: string;
}

export interface CodeLine {
  key: string | null;
  html: string;
}

export interface StandardLevel {
  level: number;
  name: string;
  description: string;
  examples: string;
}

export interface TransportMode {
  mode: string;
  description: string;
}

export interface FillingProcess {
  name: string;
  description: string;
  useCases: string;
}

export interface StorageType {
  type: string;
  description: string;
  useCases: string;
}

export interface Endpoint {
  name: string;
  method: string;
  path: string;
  description: string;
}

export interface IndicatorSet {
  name: string;
  description: string;
}

export interface Section {
  title: string;
  description?: string;
  fields?: ApiField[];
  isExpanded?: boolean;
}

export interface LcaApiDocumentation {
  apiInfo: {
    title: string;
    subtitle: string;
    description: string;
    baseUrl: string;
    httpMethod: string;
    contentType: string;
  };
  endpoints: Endpoint[];
  indicatorSets: IndicatorSet[];
  sections: {
    mainRequestObject: any;
    requestStructure: any;
    scenarioObject: any;
    packingLevels: any;
    transportation: any;
    fillingDetails: any;
    componentFillingTransportObject: any;
    distributionDetails: any;
  };
  examples: {
    request: any;
    response: any;
  };
}

interface NavSection {
  title: string;
  children?: NavChild[];
  items?: NavItem[];
}

interface NavItem {
  label: string;
  route?: string;
  isExpanded: boolean;
  children: NavChild[];
}

interface NavChild {
  label: string;
  method?: string;
  methodClass?: string;
  isActive: boolean;
  route?: string;
  sectionKey?: string;
  queryParams?: { [key: string]: string };
}

@Component({
  selector: 'piqet-angular-lca-doc',
  templateUrl: './lca-doc.component.html',
  styleUrls: ['./lca-doc.component.scss'],
})
export class LcaDocComponent implements OnInit {
  private piqetLcaApiUrl = environment.piqetLcaApiUrl;
  apiData: LcaApiDocumentation | null = null;
  activeTab: 'request' | 'response' = 'request';
  responseType: 'includeDetails' | 'impact' = 'impact';
  codeView: 'json' | 'typescript' = 'json';
  searchQuery = '';
  reqCopied = false;
  respCopied = false;

  selectedField: ApiField | null = null;
  codeLines: CodeLine[] = [];

  responseExamples: { includeDetails: any; impact: any } | null = null;

  expandedSections: { [key: string]: boolean } = {
    mainRequestObject: false,
    scenarioObject: false,
    productDetails: false,
    packingLevels: false,
    componentDetails: false,
    standardTransportation: false,
    directDistance: false,
    routeSegment: false,
    fillingDetails: false,
    fillingProcess: false,
    componentFillingTransport: false,
    distributionDetails: false,
    storageTypes: false,
    metadataObject: false,
  };

  fieldToSectionMap: { [key: string]: string } = {
    PackingLevels: 'packingLevels',
    ProductDetails: 'productDetails',
    FillingDetails: 'fillingDetails',
    WholesaleDetail: 'distributionDetails',
    RetailDetail: 'distributionDetails',
    ConsumerDetail: 'distributionDetails',
    Details: 'componentDetails',
    TransportationConfiguration: 'standardTransportation',
    DistanceBased: 'directDistance',
    LocationBased: 'routeSegment',
    RouteSegments: 'routeSegment',
    FillingProcesses: 'fillingProcess',
    ComponentWiseFillingTransportation: 'componentFillingTransport',
    Scenarios: 'scenarioObject',
  };

  navSections: NavSection[] = [
    {
      title: 'Get started',
      children: [
        { label: 'Introduction', isActive: false, sectionKey: 'overview' },
        { label: 'Base URLs & environments', isActive: false, sectionKey: 'endpoint-url' },
      ],
    },
    {
      title: 'Authentication',
      children: [{ label: 'JWT authentication', isActive: false, route: '/docs/lca-api/auth' }],
    },
    {
      title: 'LCA calculations',
      items: [
        {
          label: 'Impact calculator',
          isExpanded: true,
          children: [
            { label: 'Overview', isActive: true },
            { label: 'Main request object', isActive: false, sectionKey: 'mainRequestObject' },
            { label: 'Scenario object', isActive: false, sectionKey: 'scenarioObject' },
            { label: 'Packing levels', isActive: false, sectionKey: 'packingLevels' },
            { label: 'Product details', isActive: false, sectionKey: 'productDetails' },
            { label: 'Component details', isActive: false, sectionKey: 'componentDetails' },
            { label: 'Transportation', isActive: false, sectionKey: 'standardTransportation' },
            { label: 'Direct distance', isActive: false, sectionKey: 'directDistance' },
            { label: 'Route segments', isActive: false, sectionKey: 'routeSegment' },
            { label: 'Filling details', isActive: false, sectionKey: 'fillingDetails' },
            { label: 'Filling process', isActive: false, sectionKey: 'fillingProcess' },
            {
              label: 'Component filling transport',
              isActive: false,
              sectionKey: 'componentFillingTransport',
            },
            { label: 'Distribution', isActive: false, sectionKey: 'distributionDetails' },
          ],
        },
      ],
    },
    {
      title: 'Quick reference',
      children: [
        {
          label: 'Impact indicators',
          isActive: false,
          route: '/docs/quick-reference',
          queryParams: { section: 'indicators' },
        },
        {
          label: 'Material catalog',
          isActive: false,
          route: '/docs/quick-reference',
          queryParams: { section: 'materials' },
        },
        {
          label: 'Transport options',
          isActive: false,
          route: '/docs/quick-reference',
          queryParams: { section: 'transport-modes' },
        },
        {
          label: 'Storage options',
          isActive: false,
          route: '/docs/quick-reference',
          queryParams: { section: 'storage-types' },
        },
      ],
    },
  ];

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    public clipboardHelper: ClipboardHelper
  ) {}

  // --- Sidebar ---

  navigateToRoute(route: string): void {
    this.router.navigateByUrl(route);
  }

  toggleNavItem(item: NavItem): void {
    item.isExpanded = !item.isExpanded;
  }

  setActiveNavChild(child: NavChild): void {
    this.navSections.forEach(section => {
      section.children?.forEach(c => (c.isActive = false));
      section.items?.forEach(item => {
        item.children.forEach(c => (c.isActive = false));
      });
    });
    child.isActive = true;

    if (child.route) {
      this.router.navigate(
        [child.route],
        child.queryParams ? { queryParams: child.queryParams } : {}
      );
    } else if (child.sectionKey) {
      this.scrollToSection(child.sectionKey);
    }
  }

  get filteredNavSections(): NavSection[] {
    if (!this.searchQuery.trim()) return this.navSections;
    const q = this.searchQuery.toLowerCase();
    return this.navSections
      .map(section => ({
        ...section,
        children: section.children?.filter(c => c.label.toLowerCase().includes(q)),
        items: section.items
          ?.map(item => ({
            ...item,
            isExpanded: true,
            children: item.children.filter(c => c.label.toLowerCase().includes(q)),
          }))
          .filter(item => item.children.length > 0),
      }))
      .filter(section => (section.children?.length || 0) > 0 || (section.items?.length || 0) > 0);
  }

  // --- Sections ---

  scrollToSection(sectionKey: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.expandedSections[sectionKey] = true;
    setTimeout(() => {
      const element = document.getElementById(`section-${sectionKey}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  getSectionForField(fieldName: string): string | null {
    return this.fieldToSectionMap[fieldName] || null;
  }

  shouldShowDetailsLink(field: any): boolean {
    const dataType = field.dataType?.toLowerCase() || '';
    return (
      (dataType.includes('object') || dataType.includes('array')) &&
      this.fieldToSectionMap[field.name] !== undefined
    );
  }

  ngOnInit(): void {
    this.loadApiDocumentation();
    this.loadResponseExamples();
  }

  loadApiDocumentation(): void {
    this.http.get<LcaApiDocumentation>('assets/json/lca-api-documentation.json').subscribe({
      next: data => {
        this.apiData = data;
        this.apiData.apiInfo.baseUrl = this.piqetLcaApiUrl + '/lca/calculate-impact';
        this.rebuildCodeLines();
      },
      error: error => {
        console.error('Error loading API documentation:', error);
        this.loadFallbackData();
      },
    });
  }

  private loadFallbackData(): void {
    // Fallback data if JSON file is not accessible
  }

  loadResponseExamples(): void {
    this.http
      .get<{ includeDetails: any; impact: any }>('assets/json/lca-response-examples.json')
      .subscribe({
        next: data => {
          this.responseExamples = data;
          if (this.activeTab === 'response') this.rebuildCodeLines();
        },
        error: error => {
          console.error('Error loading response examples:', error);
        },
      });
  }

  toggleSection(sectionKey: string): void {
    this.expandedSections[sectionKey] = !this.expandedSections[sectionKey];
  }

  onSectionKeyDown(event: KeyboardEvent, sectionKey: string): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleSection(sectionKey);
    }
  }

  isSectionExpanded(sectionKey: string): boolean {
    return this.expandedSections[sectionKey] ?? false;
  }

  // --- Tabs ---

  setActiveTab(tab: 'request' | 'response'): void {
    this.activeTab = tab;
    this.rebuildCodeLines();
  }

  setResponseType(type: 'includeDetails' | 'impact'): void {
    this.responseType = type;
    this.rebuildCodeLines();
  }

  setCodeView(view: 'json' | 'typescript'): void {
    this.codeView = view;
  }

  // --- Parameter selection ---

  selectField(field: ApiField, event?: Event): void {
    if (event) event.stopPropagation();
    this.selectedField = this.selectedField === field ? null : field;
  }

  isFieldSelected(field: ApiField): boolean {
    return this.selectedField === field;
  }

  fieldsContainSelected(fields: ApiField[] | undefined): boolean {
    return !!(this.selectedField && fields && fields.indexOf(this.selectedField) !== -1);
  }

  // --- JSON ---

  getFormattedJson(obj: any): string {
    return JSON.stringify(obj, null, 2);
  }

  getCurrentResponseJson(): string {
    if (!this.responseExamples) {
      return '';
    }
    const response =
      this.responseType === 'includeDetails'
        ? this.responseExamples.includeDetails
        : this.responseExamples.impact;
    return this.getFormattedJson(response);
  }

  rebuildCodeLines(): void {
    const raw =
      this.activeTab === 'request'
        ? this.getFormattedJson(this.apiData?.examples?.request ?? {})
        : this.getCurrentResponseJson();
    this.codeLines = raw.split('\n').map(line => ({
      key: this.extractKey(line),
      html: this.colorizeJsonLine(line),
    }));
  }

  private extractKey(line: string): string | null {
    const m = line.match(/^\s*"([^"]+)"\s*:/);
    return m ? m[1] : null;
  }

  private colorizeJsonLine(line: string): string {
    const escaped = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const regex = /("(?:[^"\\]|\\.)*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?)|([{}\[\],])/g;
    return escaped.replace(regex, (match, str, colon, bool, num, punct) => {
      if (str) {
        return colon
          ? `<span class="j-key">${str}</span>${colon}`
          : `<span class="j-str">${str}</span>`;
      }
      if (bool) return `<span class="j-bool">${bool}</span>`;
      if (num) return `<span class="j-num">${num}</span>`;
      if (punct) return `<span class="j-punct">${punct}</span>`;
      return match;
    });
  }

  // --- TypeScript interface view ---

  getTypeScriptInterface(): string {
    const rootName =
      this.activeTab === 'request'
        ? 'LcaRequest'
        : this.responseType === 'includeDetails'
        ? 'LcaResponseDetailed'
        : 'LcaResponse';
    const src =
      this.activeTab === 'request'
        ? this.apiData?.examples?.request
        : this.responseType === 'includeDetails'
        ? this.responseExamples?.includeDetails
        : this.responseExamples?.impact;
    if (!src || typeof src !== 'object') return '';
    const lines: string[] = [`interface ${rootName} {`];
    this.appendTsFields(src, lines, '  ');
    lines.push('}');
    return lines.join('\n');
  }

  private appendTsFields(obj: any, lines: string[], indent: string): void {
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      const safeKey = /^[A-Za-z_$][\w$]*$/.test(k) ? k : `'${k}'`;
      if (Array.isArray(v)) {
        if (v.length > 0 && v[0] !== null && typeof v[0] === 'object') {
          lines.push(`${indent}${safeKey}: Array<{`);
          this.appendTsFields(v[0], lines, indent + '  ');
          lines.push(`${indent}}>;`);
        } else {
          const inner = v.length > 0 ? this.tsPrimitive(v[0]) : 'any';
          lines.push(`${indent}${safeKey}: ${inner}[];`);
        }
      } else if (v !== null && typeof v === 'object') {
        lines.push(`${indent}${safeKey}: {`);
        this.appendTsFields(v, lines, indent + '  ');
        lines.push(`${indent}};`);
      } else {
        lines.push(`${indent}${safeKey}: ${this.tsPrimitive(v)};`);
      }
    }
  }

  private tsPrimitive(v: any): string {
    if (v === null) return 'null';
    if (typeof v === 'string') return 'string';
    if (typeof v === 'number') return 'number';
    if (typeof v === 'boolean') return 'boolean';
    return 'any';
  }

  // --- Clipboard ---

  copyCode(): void {
    let content: string;
    if (this.codeView === 'typescript') {
      content = this.getTypeScriptInterface();
    } else if (this.activeTab === 'request') {
      content = this.getFormattedJson(this.apiData?.examples?.request);
    } else {
      content = this.getCurrentResponseJson();
    }
    this.clipboardHelper.copyCode(content);
    if (this.activeTab === 'request') {
      this.reqCopied = true;
      setTimeout(() => (this.reqCopied = false), 1400);
    } else {
      this.respCopied = true;
      setTimeout(() => (this.respCopied = false), 1400);
    }
  }

  copyUrl(text: string, event: Event): void {
    this.clipboardHelper.copyToClipboard(text, event);
  }

  isCopied(text: string): boolean {
    return this.clipboardHelper.isCopied(text);
  }

  // --- Helpers ---

  getRequiredClass(required: boolean): string {
    return required ? 'required' : 'optional';
  }

  getDataTypeClass(dataType: string): string {
    const type = (dataType || '').toLowerCase();
    if (type.includes('array') || type.includes('object')) return 'type-array-object';
    if (type.includes('string')) return 'type-string';
    if (type.includes('decimal') || type.includes('integer') || type.includes('number')) {
      return 'type-decimal';
    }
    if (type.includes('boolean')) return 'type-boolean';
    return 'type-default';
  }

  goToQuickReference(sectionId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const url = `/#/docs/quick-reference?section=${sectionId}`;
    window.open(url, '_blank');
  }
}
